import { ListContextProvider, ReduxState } from "ra-core"
import { FC, useMemo } from "react"
import { Content } from "@/components/content/Content"
import { OdResourcePageProps } from "@/types/resource"
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import useLocalListController from "@/components/list/useLocalListController"
import { Card, Divider, Box, IconButton, Tooltip, Typography } from "@mui/material"
import { Datagrid } from "@/components/list/datagrid"
import Scrollbar from "@/components/Scrollbar"
import { ObfuscationMappingResponseDto } from "@/gen/api/encrypt"
import { RecPatch } from "@/dataProvider/tools"
import { useSelector } from "react-redux"
import { AtopFuncArea, SearchField, FilterField, FilterOption } from "@/components/list/datagridFunctionWidgets"
import { FunctionField } from "@/components/field"
import { genUrlWithHashid } from "@/utils/hashid"
import { Link as RouterLink } from 'react-router-dom'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from '@mui/icons-material/Edit';
import DatabaseNameField from "@/components/databases/DatabaseNameField"
import { EncryptedDatabasesContextProvider } from "@/components/databases/useEncryptedDatabases"
import { useHasPermit } from "@/authProvider/useHasPermit"
import { EDIT_DATA_CLASSIFICATION_ENCRYPTION } from "@/constants"


const ObfuscationMappingList: FC<OdResourcePageProps> = (props) => {
  // useResourceRegister({ name: resource })
  return (
    <ObfuscationMappingListBody {...props} />
  )
}

const databaseListOptions : {sorters: Record<string, (a:ObfuscationMappingResponseDto&RecPatch, b:ObfuscationMappingResponseDto&RecPatch) => number>} = {
  sorters: {
    id: (a, b) => a.id < b.id ? -1 : 1,
    databaseName: (a, b) => (a.databaseName || '') < (b.databaseName || '') ? -1 : 1,
  }
}


const ObfuscationMappingListBody: FC<OdResourcePageProps> = ({
  options,
  resource,
}) => {
  const hasWritePermit = useHasPermit([EDIT_DATA_CLASSIFICATION_ENCRYPTION])
  const listCtrl = useLocalListController({ resource, basePath: '', filter: {} }, databaseListOptions)
  
  const data = useSelector<ReduxState, Record<number, ObfuscationMappingResponseDto>>(store => store.admin.resources[resource as string].data as any)
  const dbList = Object.values(data || {});
  
  const engineOptions = useMemo(() => Object.values(
    dbList.reduce((acc, d) => {
      const label = `${d.engineVersion || ''}${d.engineEdition ? ' ' + d.engineEdition : ''}`
      const option = ({
        label,
        value: { engineVersion: d.engineVersion, engineEdition: d.engineEdition }
      })
      acc[label] = option

      return acc
    }, {} as Record<string, {label:string,value:{engineVersion:any, engineEdition:any}}>)
  ).sort((a, b) => a.label < b.label ? -1 : 1), [ JSON.stringify([...new Set(dbList.map(d => `${d.engineVersion || ''}${d.engineEdition ? ' ' + d.engineEdition : ''}`))].sort()) ])
  
  return (
    <Content 
      pageName="Obfuscation Mapping List" 
      resourceName="Obfuscation Mapping List" 
      breadcrumbs={options?.breadcrumbs}
    >
      <ListContextProvider value={listCtrl}>
        <EncryptedDatabasesContextProvider>
          <Card>
            <AtopFuncArea >
              <SearchField 
                sourceKey="databaseName"
                match={(d: ObfuscationMappingResponseDto, fval) => !!d.databaseName && d.databaseName.toLowerCase().includes((fval as string).trim().toLowerCase())}
                sx={{ width: 280 }}
                TextFieldProps={{ placeholder: 'Search by Database Name' }}
              />
              <FilterField 
                label="Engine" 
                filterOptions={engineOptions} 
                sourceKey="engine"
                match={(d: ObfuscationMappingResponseDto, fval:FilterOption|'_empty_') => {
                  if (fval === '_empty_') return true
                  const value = fval.value
                  return d.engineVersion === value.engineVersion && d.engineEdition === value.engineEdition
                }}
                sx={{ width: 320 }}
              />
            </AtopFuncArea>
            <Divider />
            <Scrollbar options={{ suppressScrollY: true }}>
              <Box sx={{ minWidth: 1150 }}>
                <Datagrid 
                  hasBulkActions={false} 
                  sx={{ 
                    '& th' : { py: 2 },
                    '& td' : { py: 2 },
                  }}
                  {...listCtrl}
                >
                  <DatabaseNameField label="Database" keyMap={{ name: 'databaseName' }} />
                  <FunctionField 
                    source="engineVersion" 
                    label="Engine" 
                    sortable={false} 
                    render={(d?: ObfuscationMappingResponseDto & RecPatch) => (
                      <Typography
                        component="p"
                        variant="body2"
                      >
                        {d?.engineVersion + ' ' + (d?.engineEdition || '')}
                      </Typography>
                    )}
                  />
                  <FunctionField 
                    source="" 
                    label="Obfuscation Mapping Count" 
                    sortable={false} 
                    render={(d?:ObfuscationMappingResponseDto & RecPatch) => (
                      <Typography 
                        component="span" 
                        variant="h6" 
                        color={(d?.obfsMappedCount || 0) === 0 ? 'error.main' : ((d?.obfsMappedCount || 0) < 5) ? 'warning.main' : 'success.main' }
                      >
                        {d?.obfsMappedCount || 0}
                        <Typography component="span" variant="body2" sx={{ ml: 1, color: "text.primary" }}>
                          / {d?.totalCount || 0}
                        </Typography>
                      </Typography>
                    )}
                  />
                  <FunctionField 
                    label="Action"
                    sortable={false}
                    render={(d?: ObfuscationMappingResponseDto & RecPatch) => (
                      <Tooltip title={hasWritePermit ? "Edit" : "View"}>
                        <IconButton 
                          sx={{ 
                            color: 'text.primary' as any, 
                            '&:hover': { backgroundColor: 'action.hover' },
                          }}
                          component={RouterLink}
                          to={genUrlWithHashid(`/encryption/obfs/mappings/$0/show`, d?.databaseId || 0, d?.databaseName)}
                        >
                          {hasWritePermit ? <EditIcon fontSize="small" /> : <ArrowForwardIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  />
                </Datagrid>
              </Box>
            </Scrollbar>
            {//@ts-ignore 
            <DefaultPagination {...listCtrl} />}
          </Card>
        </EncryptedDatabasesContextProvider>
      </ListContextProvider>
      
    </Content>
  )
}


export default ObfuscationMappingList;
